import {
  IQuartzJobEntry,
  IQuartzJobsOverview,
} from "../../../@types/admin/AdminQuartzJobs";

/**
 * Pure helpers for the Scheduled Jobs page: the filter model, the row tone,
 * and the "what does this trigger serve" label. Kept out of the component so
 * they can be unit-tested without rendering.
 */

export type StatusTone = "active" | "inactive" | "warning" | "error";

export interface IQuartzFilterState {
  category: string;
  jobGroup: string;
  state: string;
  region: string;
  tenantId: string;
  hospitalGroupId: string;
  wardId: string;
  onlyWithWarnings: boolean;
  search: string;
}

export const EMPTY_QUARTZ_FILTERS: IQuartzFilterState = {
  category: "",
  jobGroup: "",
  state: "",
  region: "",
  tenantId: "",
  hospitalGroupId: "",
  wardId: "",
  onlyWithWarnings: false,
  search: "",
};

export function hasActiveFilter(filters: IQuartzFilterState): boolean {
  return Object.entries(filters).some(([, value]) =>
    typeof value === "boolean" ? value : value !== ""
  );
}

/** Colour of the state badge: a Normal trigger carrying warnings reads amber. */
export function stateTone(entry: IQuartzJobEntry): StatusTone {
  switch (entry.state) {
    case "Error":
      return "error";
    case "Paused":
    case "Blocked":
      return "warning";
    case "Normal":
      return entry.warnings.length > 0 ? "warning" : "active";
    default:
      return "inactive";
  }
}

/** The state label, made a little more specific than Quartz's own word. */
export function stateLabel(entry: IQuartzJobEntry): string {
  if (entry.isExecuting) return "Running";
  if (entry.state === "Normal" && entry.executionEnabled === false) {
    return "Normal · disabled by config";
  }
  return entry.state;
}

/**
 * What the row is for. A workflow trigger names its workflow; a ward sync
 * trigger names the ward; a static regional job just names its region.
 */
export function targetLabel(entry: IQuartzJobEntry): string {
  if (entry.workflow) {
    if (!entry.workflow.found) {
      return `Workflow ${entry.workflow.workflowId} (missing)`;
    }
    const suffix = entry.workflow.published === false ? " (unpublished)" : "";
    return `${entry.workflow.title ?? entry.workflow.workflowId}${suffix}`;
  }

  if (entry.wards.length > 0) {
    return entry.wards
      .map((ward) => {
        if (!ward.found) return `Ward ${ward.wardId} (missing)`;
        return ward.wardName ?? `Ward ${ward.wardId}`;
      })
      .join(", ");
  }

  if (!entry.hasTrigger) return "No trigger";
  return entry.region ? `All wards (${entry.region})` : "—";
}

/** Distinct hospital group names across the entry's wards. */
export function hospitalLabel(entry: IQuartzJobEntry): string {
  const names = Array.from(
    new Set(
      entry.wards
        .map((ward) => ward.hospitalGroupName)
        .filter((name): name is string => Boolean(name))
    )
  );
  return names.join(", ");
}

/** Distinct tenant names across the entry's wards. */
export function tenantLabel(entry: IQuartzJobEntry): string {
  const names = Array.from(
    new Set(
      entry.wards
        .map((ward) => ward.tenantName)
        .filter((name): name is string => Boolean(name))
    )
  );
  return names.join(", ");
}

/** The schedule in one line: plain English when known, else the raw cron. */
export function scheduleLabel(entry: IQuartzJobEntry): string {
  if (!entry.hasTrigger) return "";
  const base = entry.scheduleDescription ?? entry.cronExpression ?? "";
  if (!base) return entry.triggerType ?? "";
  const tz =
    entry.timeZoneId && entry.timeZoneId !== "UTC"
      ? ` (${entry.timeZoneId})`
      : "";
  return `${base}${tz}`;
}

/**
 * Compact relative time ("in 2h 05m", "3m ago"), for the next/last fire
 * columns where an absolute stamp alone makes the reader do arithmetic.
 */
export function relativeTime(
  iso: string | null | undefined,
  now: Date = new Date()
): string {
  if (!iso) return "";
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return "";

  const diffMs = target - now.getTime();
  const future = diffMs >= 0;
  const totalMinutes = Math.round(Math.abs(diffMs) / 60_000);

  let span: string;
  if (totalMinutes < 1) {
    span = "under a minute";
  } else if (totalMinutes < 60) {
    span = `${totalMinutes}m`;
  } else if (totalMinutes < 60 * 48) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    span = `${hours}h ${String(minutes).padStart(2, "0")}m`;
  } else {
    span = `${Math.round(totalMinutes / (60 * 24))}d`;
  }

  return future ? `in ${span}` : `${span} ago`;
}

function includesTerm(value: string | null | undefined, term: string): boolean {
  return Boolean(value) && String(value).toLowerCase().includes(term);
}

/**
 * Client-side filtering, mirroring the API's query filters (AND semantics)
 * and the API's search haystack (QuartzJobsService.Matches) — keep the two
 * lists of fields in step. Selects match exactly; the search box matches
 * job, trigger, workflow, ward, hospital group, tenant, cron and schedule text.
 */
export function filterEntries(
  entries: IQuartzJobEntry[],
  filters: IQuartzFilterState
): IQuartzJobEntry[] {
  const term = filters.search.trim().toLowerCase();

  return entries.filter((entry) => {
    if (filters.category && entry.category !== filters.category) return false;
    if (filters.jobGroup && entry.jobGroup !== filters.jobGroup) return false;
    if (filters.state && entry.state !== filters.state) return false;
    if (filters.region && entry.region !== filters.region) return false;
    if (filters.tenantId && !entry.tenantIds.includes(filters.tenantId)) {
      return false;
    }
    if (
      filters.hospitalGroupId &&
      !entry.hospitalGroupIds.includes(Number(filters.hospitalGroupId))
    ) {
      return false;
    }
    if (filters.wardId && !entry.wardIds.includes(Number(filters.wardId))) {
      return false;
    }
    if (filters.onlyWithWarnings && entry.warnings.length === 0) return false;

    if (!term) return true;

    const haystack: (string | null | undefined)[] = [
      entry.jobName,
      entry.jobGroup,
      entry.jobType,
      entry.triggerName,
      entry.triggerGroup,
      entry.cronExpression,
      entry.scheduleDescription,
      entry.timeZoneId,
      entry.state,
      entry.region,
      entry.workflow?.title,
      entry.workflow?.workflowId,
      ...entry.wards.map((ward) => ward.wardName),
      ...entry.wards.map((ward) => String(ward.wardId)),
      ...entry.wards.map((ward) => ward.hospitalGroupName),
      ...entry.wards.map((ward) => ward.tenantName),
    ];
    return haystack.some((value) => includesTerm(value, term));
  });
}

/**
 * Wards for the ward dropdown, narrowed to the chosen hospital group when one
 * is selected — the option lists come unfiltered from the API.
 */
export function wardOptionsFor(
  overview: IQuartzJobsOverview | undefined,
  hospitalGroupId: string
) {
  const wards = overview?.options.wards ?? [];
  if (!hospitalGroupId) return wards;
  return wards.filter(
    (ward) => String(ward.hospitalGroupId ?? "") === hospitalGroupId
  );
}
